import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IAnalyticsAction, IApplicationState } from '../../store';
import { BookingStatus } from '../../store/Booking';
import { actionCreators as PlayerActions } from '../../store/Players';
import { actionCreators as PaymentOptionActions } from '../../store/PaymentOption';
import { GoogleTagManagerEvents } from '../../helpers/googleTagManager';
import { DISCOUNT_TIERS } from './tieredGroupSizeDiscountConfig';

const useTieredGroupSizeDiscountAccept = (): (newPlayerCount: number) => boolean => {
	const dispatch = useDispatch();
	const players = useSelector((state: IApplicationState) => state.players.selectedPlayers);
	const pendingBooking = useSelector((state: IApplicationState) => state.booking.booking);
	const bookingStatus = useSelector((state: IApplicationState) => state.booking.status);
	const selectedPaymentOption = useSelector((state: IApplicationState) => state.paymentOption.selectedOption);

	return useCallback(
		(newPlayerCount: number): boolean => {
			const previousCount = players ?? 0;

			// block player updates entirely when the booking is already reserved
			// the widget should already be hidden, but this is a safety guard
			if (pendingBooking && bookingStatus === BookingStatus.Pending) {
				return false;
			}

			// BEFORE RESERVATION: no booking exists yet, so update the player
			// count immediately. The count will be used when reserveBooking is
			// called later at checkout.
			dispatch(PlayerActions.selectPlayers(newPlayerCount));

			dispatch({
				type: 'TIERED_GROUP_SIZE_DISCOUNT_PLAYERS_UPDATED',
				googleTagManagerEvent: GoogleTagManagerEvents.TieredGroupSizeDiscountPlayersUpdated(previousCount, newPlayerCount),
			} as IAnalyticsAction);

			if (DISCOUNT_TIERS.some((tier) => newPlayerCount >= tier.threshold)) {
				dispatch({
					type: 'TIERED_GROUP_SIZE_DISCOUNT_OFFER_ACCEPTED',
					googleTagManagerEvent: GoogleTagManagerEvents.TieredGroupSizeDiscountOfferAccepted(previousCount, newPlayerCount),
				} as IAnalyticsAction);
			}

			if (selectedPaymentOption === 'split') {
				dispatch(PaymentOptionActions.resetPaymentOption());
			}

			if (selectedPaymentOption === 'deposit') {
				dispatch(PaymentOptionActions.resetPaymentOption());
			}

			return true;
		},
		[dispatch, pendingBooking, bookingStatus, players, selectedPaymentOption],
	);
};

export default useTieredGroupSizeDiscountAccept;
